import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Link, useLocation } from "react-router-dom";
import { Shield, LogOut, LayoutDashboard, FileText, Brain, Users, Activity, Car, Heart, Sun, Moon, HelpCircle, BookOpen, Scale, Menu, ChevronRight, Package } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "../components/ui/sheet";
import NotificationBell from "./NotificationBell";
import HealthPulse from "./HealthPulse";
import LanguagePicker from "./LanguagePicker";

const roleLabels = {
  system_admin: "System Admin",
  govt_admin: "Govt Admin",
  user: "Citizen",
};

const roleBadgeStyles = {
  system_admin: "bg-black text-white hover:bg-black",
  govt_admin: "bg-gray-800 text-white hover:bg-gray-800",
  user: "bg-gray-200 text-gray-800 hover:bg-gray-200",
};

export default function Header({ notifications = [], onNotificationRead }) {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === "system_admin" || user?.role === "govt_admin";

  const navItems = [
    { path: "/dashboard", label: t("header.dashboard"), icon: LayoutDashboard },
    { path: "/reports", label: t("header.reports"), icon: FileText },
    { path: "/intelligence", label: t("header.intelligence"), icon: Brain },
    { path: "/epass", label: t("header.epass"), icon: Car },
    ...(isAdmin ? [
      { path: "/admin/users", label: t("header.users"), icon: Users },
      { path: "/admin/activity", label: t("header.activity"), icon: Activity },
      { path: "/admin/stickers", label: "Stickers", icon: Package },
    ] : []),
  ];

  return (
    <header className="h-12 sm:h-14 bg-white border-b border-gray-200 flex items-center justify-between px-2 sm:px-4 md:px-6 gap-2" data-testid="app-header">
      {/* Left - logo + desktop nav */}
      <div className="flex items-center gap-2 sm:gap-6 min-w-0 flex-1">
        <Link to="/dashboard" className="flex items-center gap-1.5 shrink-0" data-testid="header-logo">
          <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-black" strokeWidth={2} />
          <span className="font-heading text-base sm:text-lg font-black tracking-tighter">GUARD</span>
        </Link>
        {/* Desktop-only inline nav — icon-only until xl to avoid overflow
            on iPads / small laptops; labels return on wide screens. */}
        <nav className="hidden lg:flex items-center gap-0.5 min-w-0">
          {navItems.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              title={label}
              data-testid={`nav-${label.toLowerCase()}`}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-sm transition-colors duration-150 shrink-0 ${
                location.pathname === path
                  ? "bg-black text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden xl:inline">{label}</span>
            </Link>
          ))}
        </nav>
      </div>

      {/* Right - donate (always) + desktop chrome + mobile hamburger */}
      <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
        <Link
          to="/donate"
          className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-bold rounded-sm transition-colors duration-150 bg-rose-600 text-white hover:bg-rose-700 shrink-0"
          data-testid="nav-donate"
          aria-label={t("header.donate")}
        >
          <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" />
          <span className="inline">{t("header.donate")}</span>
        </Link>

        <NotificationBell notifications={notifications} onRead={onNotificationRead} />

        {/* Desktop-only chrome — from lg up; below that everything lives in
            the hamburger sheet so nothing overlaps. */}
        <div className="hidden lg:flex items-center gap-2">
          <LanguagePicker />
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-sm hover:bg-gray-100 text-gray-600 transition-colors shrink-0"
            data-testid="theme-toggle"
            aria-label={theme === "dark" ? t("header.toggle_theme_light") : t("header.toggle_theme_dark")}
            title={theme === "dark" ? t("header.toggle_theme_light") : t("header.toggle_theme_dark")}
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <HealthPulse />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-1.5 rounded-sm hover:bg-gray-100 text-gray-600 transition-colors shrink-0"
                data-testid="help-menu-trigger"
                aria-label={t("header.help_policies", { defaultValue: "Help and policies" })}
                title={t("header.help_policies", { defaultValue: "Help & policies" })}
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-sm" data-testid="help-menu">
              <DropdownMenuItem asChild className="cursor-pointer rounded-none focus:bg-gray-50">
                <Link to="/terms" data-testid="help-terms-link" className="flex items-center gap-2">
                  <Scale className="w-4 h-4 text-gray-500" />
                  <div className="flex flex-col">
                    <span className="text-sm font-body">Terms &amp; Conditions</span>
                    <span className="text-[10px] text-gray-400">How Guard works, your rights</span>
                  </div>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer rounded-none focus:bg-gray-50">
                <a
                  href="/Guard_Technical_Paper.pdf"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="help-paper-link"
                  className="flex items-center gap-2"
                >
                  <BookOpen className="w-4 h-4 text-gray-500" />
                  <div className="flex flex-col">
                    <span className="text-sm font-body">Technical paper</span>
                    <span className="text-[10px] text-gray-400">Architecture &amp; capabilities (PDF)</span>
                  </div>
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user && (
            <Link
              to="/trust"
              className="flex items-center gap-2 hover:bg-gray-100 rounded-sm px-1 py-0.5 transition-colors"
              data-testid="nav-trust"
              title="My Trust"
            >
              <img
                src={user.picture || `https://ui-avatars.com/api/?name=${user.name}&background=111827&color=fff&size=32`}
                alt={user.name}
                className="w-7 h-7 rounded-full border border-gray-300"
                data-testid="user-avatar"
              />
              <span className="text-sm font-medium text-gray-800 hidden 2xl:inline" data-testid="user-name">
                {user.name}
              </span>
              <Badge
                className={`text-[10px] px-1.5 py-0 rounded-sm ${roleBadgeStyles[user.role] || roleBadgeStyles.user}`}
                data-testid="user-role-badge"
              >
                {roleLabels[user.role] || "Citizen"}
              </Badge>
            </Link>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-gray-500 hover:text-black hover:bg-gray-100 rounded-sm h-8 w-8 p-0"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>

        {/* Mobile hamburger - opens slide-in menu with all nav + actions */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              className="lg:hidden p-1.5 rounded-sm hover:bg-gray-100 text-gray-700 transition-colors shrink-0"
              data-testid="mobile-menu-trigger"
              aria-label={t("header.open_menu")}
            >
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[88%] max-w-sm p-0 flex flex-col" data-testid="mobile-menu">
            {/* Profile header */}
            {user && (
              <Link
                to="/trust"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                data-testid="mobile-nav-trust"
              >
                <img
                  src={user.picture || `https://ui-avatars.com/api/?name=${user.name}&background=111827&color=fff&size=64`}
                  alt={user.name}
                  className="w-11 h-11 rounded-full border border-gray-300"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-gray-900 truncate">{user.name}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Badge className={`text-[10px] px-1.5 py-0 rounded-sm ${roleBadgeStyles[user.role] || roleBadgeStyles.user}`}>
                      {roleLabels[user.role] || "Citizen"}
                    </Badge>
                    <span className="text-[10px] text-gray-500">View trust</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Link>
            )}

            {/* Primary nav */}
            <nav className="flex flex-col py-2" data-testid="mobile-nav">
              {navItems.map(({ path, label, icon: Icon }) => {
                const active = location.pathname === path;
                return (
                  <SheetClose asChild key={path}>
                    <Link
                      to={path}
                      data-testid={`mobile-nav-${label.toLowerCase()}`}
                      className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                        active
                          ? "bg-black text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{label}</span>
                    </Link>
                  </SheetClose>
                );
              })}
            </nav>

            <div className="border-t border-gray-200 my-1" />

            {/* Secondary actions */}
            <div className="flex flex-col py-1">
              <button
                onClick={() => { toggleTheme(); }}
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors text-left"
                data-testid="mobile-theme-toggle"
              >
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                <span>{theme === "dark" ? t("header.toggle_theme_light") : t("header.toggle_theme_dark")}</span>
              </button>

              <div className="px-4 py-3 border-y border-gray-200/60">
                <LanguagePicker className="w-full justify-start" />
              </div>

              <SheetClose asChild>
                <Link
                  to="/terms"
                  data-testid="mobile-help-terms-link"
                  className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Scale className="w-4 h-4" />
                  <span>Terms &amp; Conditions</span>
                </Link>
              </SheetClose>

              <a
                href="/Guard_Technical_Paper.pdf"
                target="_blank"
                rel="noreferrer"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-help-paper-link"
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                <span>Technical paper</span>
              </a>

              <div className="flex items-center gap-3 px-4 py-3 text-sm text-gray-600">
                <HelpCircle className="w-4 h-4" />
                <span className="flex-1">System status</span>
                <HealthPulse />
              </div>
            </div>

            {/* Logout pinned to bottom */}
            <div className="mt-auto border-t border-gray-200 p-3">
              <Button
                variant="outline"
                onClick={() => { setMobileOpen(false); logout(); }}
                className="w-full justify-center gap-2 rounded-sm"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-4 h-4" /> {t("header.logout")}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
